/* Nemira — PDP sticky buy-bar.
   Canonical pattern: variant status, price with its compare-at, and the CTA.
   No selector in the bar - that overcrowds it on mobile and duplicates the buy
   box; the reader scrolls back to change quantity and the bar mirrors it.

   Two things this has to work around in the ge-pi section:
   - it sets .textContent on the price node, which wipes any child we append, so
     the compare-at lives in its own sibling node
   - its own show/hide trigger is not tied to the real add-to-cart button, so we
     gate visibility ourselves with an IntersectionObserver on that button
   Safe to delete. */
(function () {
  'use strict';

  var BAR = '.pi-sticky-ge-pi';
  var TIER = '.pi-tier';
  var PAGE_ATC = '.pi-tiers-row, .pi-atc, .nm-cta';

  function attr(el, n) { return el ? (el.getAttribute(n) || '').trim() : ''; }

  function build(bar) {
    if (bar.getAttribute('data-nm-enhanced')) return;
    var info = bar.querySelector('.pi-sticky-info-ge-pi');
    var price = bar.querySelector('[data-sticky-price]');
    if (!info || !price) return;

    bar.setAttribute('data-nm-enhanced', '1');
    bar.classList.add('nm-psb');

    var hero = document.querySelector('.pi-gallery img, .pi-media img, .product img');
    var thumb = document.createElement('img');
    thumb.className = 'nm-psb-thumb';
    thumb.alt = '';
    thumb.width = 42; thumb.height = 42; thumb.loading = 'lazy';
    thumb.src = hero ? (hero.currentSrc || hero.src || '') : '';
    if (thumb.src) bar.insertBefore(thumb, bar.firstChild);

    // each tier has its own shot - one, two or three cushions - so the bar shows
    // what is actually being added rather than the gallery hero
    function tierImage(tier) {
      var t = tier && tier.querySelector('.pi-tier-img img, img');
      return t ? (t.currentSrc || t.src || '') : '';
    }

    // sibling, not a child of the price node - see the note above
    var money = document.createElement('div');
    money.className = 'nm-psb-money';
    price.parentNode.insertBefore(money, price.nextSibling);
    money.appendChild(price);
    var was = document.createElement('span');
    was.className = 'nm-psb-was';
    money.appendChild(was);

    function syncPrice() {
      var active = document.querySelector(TIER + '.selected') ||
                   document.querySelector(TIER + '.is-selected');
      var cmp = attr(active, 'data-compare');
      was.textContent = cmp || '';
      was.hidden = !cmp;
      var ti = tierImage(active);
      if (ti && thumb.getAttribute('src') !== ti) thumb.src = ti;
      if (thumb.src && !thumb.parentNode) bar.insertBefore(thumb, bar.firstChild);
    }

    new MutationObserver(syncPrice)
      .observe(document.body, { attributes: true, attributeFilter: ['class'], subtree: true });
    syncPrice();

    // --- visibility: only once the page's own add-to-cart has gone by ---
    var triggers = Array.prototype.slice.call(document.querySelectorAll(PAGE_ATC));
    if (!triggers.length) { bar.classList.add('nm-show'); return; }

    // The bar belongs below the buy button, never before it. "Not intersecting"
    // is true both above and below an element, so state has to tell those apart:
    // it appears once the buy controls have gone past, and stands down whenever
    // any of them is back on screen.
    var state = new WeakMap();

    function classify(el) {
      var r = el.getBoundingClientRect();
      if (r.bottom <= 0) return 'above';
      if (r.top >= (window.innerHeight || 0)) return 'below';
      return 'in';
    }

    function apply() {
      var inView = false, passed = false;
      triggers.forEach(function (t) {
        var v = state.get(t);
        if (v === 'in') inView = true;
        else if (v === 'above') passed = true;
      });
      var show = passed && !inView;
      bar.classList.toggle('nm-show', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if (!('IntersectionObserver' in window)) { bar.classList.add('nm-show'); return; }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        state.set(e.target, e.isIntersecting ? 'in' : classify(e.target));
      });
      apply();
    }, { threshold: 0 });

    triggers.forEach(function (t) { state.set(t, classify(t)); io.observe(t); });
    apply();

    // an element that stays off screen fires no further entries, so the moment
    // it goes from ahead to behind has to be caught on scroll
    var queued = false;
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () {
        queued = false;
        triggers.forEach(function (t) { state.set(t, classify(t)); });
        apply();
      });
    }, { passive: true });
  }

  /* ---- tier cards: complete the comparison, and make them operable ---- */
  function tiers() {
    var row = document.querySelector('.pi-tiers-row');
    if (!row || row.getAttribute('data-nm-tiers')) return;
    var cards = Array.prototype.slice.call(row.querySelectorAll('.pi-tier'));
    if (!cards.length) return;
    row.setAttribute('data-nm-tiers', '1');

    cards.forEach(function (card) {
      // the single had one chip and no row to belong to, so it aligned with
      // neither band. Naming it the base price completes the ladder - base,
      // save, save - without inventing a benefit it does not have.
      if (attr(card, 'data-qty') === '1' && !card.querySelector('.pi-tier-support')) {
        var host = card.querySelector('.pi-row-content') || card.querySelector('.pi-tier-inner');
        var va = card.querySelector('.pi-tier-value-add');
        if (host) {
          var base = document.createElement('div');
          base.className = 'pi-tier-support nm-base';
          base.textContent = 'Base price';
          host.insertBefore(base, va || null);
        }
      }
    });

    // functionally a radio group, semantically nothing: no role, no tab stop,
    // no state. A keyboard user could not choose a bundle at all.
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'How many cushions');

    function selectedIndex() {
      for (var i = 0; i < cards.length; i++) {
        if (cards[i].classList.contains('selected') || cards[i].classList.contains('is-selected')) return i;
      }
      return 0;
    }

    function paint() {
      var sel = selectedIndex();
      cards.forEach(function (c, i) {
        c.setAttribute('role', 'radio');
        c.setAttribute('aria-checked', i === sel ? 'true' : 'false');
        c.setAttribute('tabindex', i === sel ? '0' : '-1');
        var label = attr(c, 'data-label'), price = attr(c, 'data-price');
        if (label) c.setAttribute('aria-label', price ? label + ', ' + price : label);
      });
    }

    function choose(i) {
      i = (i + cards.length) % cards.length;
      var t = cards[i];
      (t.querySelector('.pi-tier-inner') || t).click();
      window.setTimeout(function () { paint(); cards[i].focus(); }, 40);
    }

    cards.forEach(function (card, i) {
      card.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k === 'ArrowRight' || k === 'ArrowDown') { e.preventDefault(); choose(i + 1); }
        else if (k === 'ArrowLeft' || k === 'ArrowUp') { e.preventDefault(); choose(i - 1); }
        else if (k === ' ' || k === 'Enter') { e.preventDefault(); choose(i); }
      });
    });

    new MutationObserver(paint).observe(row, {
      attributes: true, attributeFilter: ['class'], subtree: true
    });
    paint();
  }

  /* a rating line immediately above the buy button - proof where the decision
     is actually made, rather than only at the top of the page */
  function atcProof() {
    var cfg = window.NEMIRA_PDP_STICKY;
    // No real review data set for this product yet - don't fabricate a
    // rating/count. Set window.NEMIRA_PDP_STICKY = {score, proof} on a
    // product's page once it has real verified reviews.
    if (!cfg || !cfg.score || !cfg.proof) return;
    var btns = document.querySelectorAll('.pi-atc, .nm-cta');
    if (!btns.length) return;
    var stars = '';
    for (var i = 0; i < 5; i++) {
      stars += '<svg viewBox="1.5 1.4 17 17.2" aria-hidden="true"><path d="M10 1.6l2.5 5.1 5.6.8-4 4 .9 5.6-5-2.7-5 2.7.9-5.6-4-4 5.6-.8z"/></svg>';
    }
    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.previousElementSibling &&
          btn.previousElementSibling.classList.contains('nm-atc-proof')) return;
      var p = document.createElement('div');
      p.className = 'nm-atc-proof';
      p.innerHTML = '<span class="nm-atc-stars" role="img" aria-label="' +
        cfg.score + ' out of 5">' + stars + '</span>' +
        '<span><b>' + cfg.score + '</b> <i>&middot;</i> ' +
        cfg.proof + '</span>';
      btn.parentNode.insertBefore(p, btn);
    });
  }

  /* Where you can and cannot get it.
     Retailer names in type, never their logos: naming a company to state a true
     fact about your own product is nominative use, but reproducing their mark -
     struck through or not - implies a relationship they have not granted.
     The reason-why line matters as much as the exclusivity: "not in shops" only
     reads as a benefit once you say why. */
  function exclusivity() {
    if (document.querySelector('.nm-excl')) return;
    // It belongs at the close, not under the buy box. High on the page the
    // reader has not decided they want it yet, so "you can only get it here"
    // is noise competing with the add-to-cart. By the final CTA they have had
    // the price ladder and the offer, and the one exit left is "I'll look
    // around" - which is exactly what this shuts.
    // Anchor by marker, not by position. The closing offer block carries
    // [data-nm-cta]; the cost card's button is [data-nm-cost-cta]. Ordinal
    // matching put this block in the cost card, because the offer block's
    // button is not in the DOM yet on the first pass. If the real anchor is
    // not there yet, do nothing - the observer calls this again.
    var finalCta = document.querySelector('[data-nm-cta]');
    if (!finalCta) return;
    var anchorEl = finalCta;
    var prev = finalCta.previousElementSibling;
    if (prev && prev.classList.contains('nm-atc-proof')) anchorEl = prev;

    var cfg = window.NEMIRA_PDP_EXCLUSIVE || {};
    var rows = cfg.rows || [
      ['no', 'Amazon', ''],
      ['no', 'Walmart', ''],
      ['no', 'Truck stops and travel centers', ''],
      ['yes', 'trynemira.com', 'Only here']
    ];

    var cross = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';
    var tick = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>';

    var html = '<p class="nm-excl-h">' +
      (cfg.heading || 'You can&rsquo;t pick this up at a truck stop.') + '</p><ul class="nm-excl-l">';
    rows.forEach(function (r) {
      var yes = r[0] === 'yes';
      html += '<li class="' + (yes ? 'is-yes' : 'is-no') + '">' +
        (yes ? tick : cross) +
        '<span class="nm-excl-w">' + r[1] + '</span>' +
        (r[2] ? '<span class="nm-excl-n">' + r[2] + '</span>' : '') +
        '</li>';
    });
    html += '</ul><p class="nm-excl-f">' +
      (cfg.foot || 'Direct is what keeps the price down instead of a retail markup - and why the 60 days come from us, not a returns desk.') +
      '</p>';

    var box = document.createElement('div');
    box.className = 'nm-excl';
    box.innerHTML = html;
    anchorEl.parentNode.insertBefore(box, anchorEl);
  }

  function boot() {
    tiers();
    atcProof();
    exclusivity();
    var bar = document.querySelector(BAR);
    if (bar) { build(bar); return true; }
    return false;
  }

  boot();
  // the lower add-to-cart lives in a section that renders after this runs, so
  // keep looking for a while rather than assuming one pass caught everything
  var mo = new MutationObserver(function () { tiers(); atcProof(); exclusivity(); boot(); });
  mo.observe(document.documentElement, { childList: true, subtree: true });
  window.setTimeout(function () { mo.disconnect(); atcProof(); }, 12000);
  [400, 1200, 3000].forEach(function (t) {
    window.setTimeout(atcProof, t); window.setTimeout(exclusivity, t);
  });
})();
